import React, { useEffect, useMemo, useState } from "react"
import { Spinner } from "reactstrap"
import Skeleton from "react-loading-skeleton"
import {
    ArrowLeft, MessageSquareText, AlertTriangle, Info, Lock, Hourglass, Sparkles, Clock,
} from "lucide-react"
import { SENTIMENT_STATUS } from "../../hooks/useSentimentUgc"
import { SENTIMENT_PLATFORMS, SENTIMENT_STAGE_LABEL } from "../../constants"
import {
    Card, SectionTitle, SentimentChip, SentimentBar, SentimentLegend, NetScore, NetScoreTrack,
    PlatformBadge, ProgressSteps, UpgradeNotice, PurchaseIntentMeter,
} from "./SentimentParts"
import { getPlatformEntries, formatNetScore, formatSentimentDate, getNextSentimentRefresh } from "./sentimentUtils"
import PlatformSentimentDetail from "./PlatformSentimentDetail"

const STAT_LABEL_STYLE = { fontSize: "12px", color: "#9ca3af", fontWeight: 500 }
const STAT_VALUE_STYLE = { fontSize: "22px", color: "#fff", fontWeight: 700, lineHeight: 1.2, fontVariantNumeric: "tabular-nums" }

/** One platform's tile in the comparison row - its state, score and mini distribution. */
const PlatformSummaryCard = ({ entry, selected, onSelect }) => {
    const { id, data, stage, blocked } = entry
    const status = data?.status
    const isCompleted = status === "completed"
    const sentiment = data?.sentiment || {}

    let content
    if (isCompleted) {
        content = (
            <>
                <div className="d-flex align-items-end justify-content-between gap-2 mb-3">
                    <div>
                        <div style={STAT_LABEL_STYLE}>Net sentiment</div>
                        <div style={{ ...STAT_VALUE_STYLE, fontSize: "26px" }}>{formatNetScore(sentiment.score)}</div>
                    </div>
                    <SentimentChip sentiment={data.overall_sentiment} size="sm" />
                </div>
                <SentimentBar percent={sentiment.percent} counts={sentiment.counts} height={8} />
                <div className="mt-2">
                    <SentimentLegend percent={sentiment.percent} compact />
                </div>
                <div className="d-flex align-items-center justify-content-between gap-2 mt-3 pt-3" style={{ borderTop: "1px solid rgba(255,255,255,0.05)", fontSize: "12px", color: "#9ca3af" }}>
                    <span>{data.comments_analysed ?? sentiment.labelled ?? 0} of {data.comments_found ?? "—"} comments</span>
                    {data.analysis?.purchase_intent && (
                        <span className="d-inline-flex align-items-center gap-2">
                            Intent <PurchaseIntentMeter value={data.analysis.purchase_intent} />
                        </span>
                    )}
                </div>
            </>
        )
    } else if (status === "pending" || stage) {
        content = (
            <div className="mt-2">
                <div className="d-flex align-items-center gap-2 mb-3" style={{ color: "#e8e8e8", fontSize: "13px" }}>
                    <Spinner size="sm" style={{ width: 14, height: 14, color: "#F6255A" }} />
                    {SENTIMENT_STAGE_LABEL[stage] || "Queued"}…
                </div>
                <ProgressSteps stage={stage || "queued"} />
            </div>
        )
    } else if (status === "insufficient_data") {
        content = (
            <div className="d-flex align-items-start gap-2 mt-2" style={{ color: "#9ca3af", fontSize: "13px", lineHeight: 1.5 }}>
                <Info size={15} style={{ flexShrink: 0, marginTop: "2px" }} />
                <div>
                    <div style={{ color: "#e8e8e8", fontWeight: 600, marginBottom: "2px" }}>Not enough comments</div>
                    {data.reason || (data.comments_fetch_skipped
                        ? "Its top videos don't have enough comments to analyse yet."
                        : `At least ${data.min_comments ?? 20} usable comments are needed.`)}
                </div>
            </div>
        )
    } else if (status === "expired" || blocked) {
        content = (
            <div className="d-flex align-items-start gap-2 mt-2" style={{ color: "#9ca3af", fontSize: "13px", lineHeight: 1.5 }}>
                <Lock size={15} color="#F6255A" style={{ flexShrink: 0, marginTop: "2px" }} />
                <div>
                    <div style={{ color: "#e8e8e8", fontWeight: 600, marginBottom: "2px" }}>Expired</div>
                    Upgrade to regenerate this platform's sentiment.
                </div>
            </div>
        )
    } else {
        content = (
            <div className="d-flex align-items-start gap-2 mt-2" style={{ color: "#9ca3af", fontSize: "13px", lineHeight: 1.5 }}>
                <AlertTriangle size={15} color="#ef4444" style={{ flexShrink: 0, marginTop: "2px" }} />
                <div>
                    <div style={{ color: "#e8e8e8", fontWeight: 600, marginBottom: "2px" }}>Analysis failed</div>
                    {data?.reason || data?.error || "This platform will be retried automatically in a few hours."}
                </div>
            </div>
        )
    }

    return (
        <div
            role={isCompleted ? "button" : undefined}
            tabIndex={isCompleted ? 0 : undefined}
            onClick={isCompleted ? () => onSelect(id) : undefined}
            onKeyDown={isCompleted ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(id) } } : undefined}
            className={`ugc-sent-platform-card${isCompleted ? " is-clickable" : ""}${selected ? " is-selected" : ""}`}
        >
            <div className="d-flex align-items-center justify-content-between gap-2">
                <PlatformBadge id={id} />
                {selected && <span style={{ fontSize: "10.5px", fontWeight: 700, color: "#F6255A", letterSpacing: "0.04em" }}>VIEWING</span>}
            </div>
            {content}
        </div>
    )
}

const RunningState = ({ appName, data }) => {
    const entries = getPlatformEntries(data)
    const shown = entries.length > 0
        ? entries
        : SENTIMENT_PLATFORMS.map(id => ({ id, data: { status: "pending" }, stage: "queued", blocked: false }))
    return (
        <>
            <div style={{
                display: "flex", alignItems: "center", gap: "12px",
                background: "rgba(246,37,90,0.06)", border: "1px solid rgba(246,37,90,0.2)",
                borderRadius: "12px", padding: "14px 18px", marginBottom: "20px",
            }}>
                <Spinner size="sm" style={{ color: "#F6255A", flexShrink: 0 }} />
                <span style={{ color: "#e8e8e8", fontSize: "13.5px", lineHeight: 1.5 }}>
                    Analysing audience comments for <strong>{appName}</strong>. A first run can take several minutes when a platform's
                    videos still need to be collected - you can keep browsing videos, we'll keep checking automatically.
                </span>
            </div>
            <div className="ugc-sent-platform-grid">
                {shown.map(entry => <PlatformSummaryCard key={entry.id} entry={entry} selected={false} onSelect={() => {}} />)}
            </div>
        </>
    )
}

const LoadingState = () => (
    <div className="d-flex flex-column gap-3">
        <div className="ugc-sent-overview-grid">
            {[0, 1].map(i => (
                <Card key={i}>
                    <Skeleton width="40%" baseColor="#2a2a35" highlightColor="#33333f" />
                    <Skeleton height={44} width="55%" style={{ marginTop: 10 }} baseColor="#2a2a35" highlightColor="#33333f" />
                    <Skeleton height={10} style={{ marginTop: 16 }} baseColor="#2a2a35" highlightColor="#33333f" />
                </Card>
            ))}
        </div>
        <div className="ugc-sent-platform-grid">
            {[0, 1, 2].map(i => (
                <Card key={i}>
                    <Skeleton width="50%" baseColor="#2a2a35" highlightColor="#33333f" />
                    <Skeleton height={28} width="35%" style={{ marginTop: 12 }} baseColor="#2a2a35" highlightColor="#33333f" />
                    <Skeleton height={8} style={{ marginTop: 12 }} baseColor="#2a2a35" highlightColor="#33333f" />
                </Card>
            ))}
        </div>
    </div>
)

/**
 * Full comment-sentiment view for the selected app - swapped in place of the
 * video grid (same "swap the panel" pattern as VideoDetailSection), since the
 * sentiment covers every platform at once rather than the active tab.
 */
const SentimentPanel = ({ app, sentiment, onBack, onGenerate }) => {
    const { status, data, error, isRunning } = sentiment
    const entries = useMemo(() => getPlatformEntries(data), [data])
    const completed = entries.filter(e => e.data?.status === "completed")
    const [selectedPlatform, setSelectedPlatform] = useState(null)

    // Keep the deep dive pointed at a platform that actually has results -
    // e.g. the first one to finish while others are still running.
    useEffect(() => {
        if (completed.length === 0) {
            if (selectedPlatform) setSelectedPlatform(null)
            return
        }
        if (!completed.some(e => e.id === selectedPlatform)) setSelectedPlatform(completed[0].id)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [completed.map(e => e.id).join(",")])

    const overall = data?.overall
    const selectedEntry = completed.find(e => e.id === selectedPlatform)
    const generatedDates = completed.map(e => e.data.generated_at).filter(Boolean).sort()
    const lastGenerated = generatedDates.length ? formatSentimentDate(generatedDates[generatedDates.length - 1]) : null
    const nextRefreshAt = getNextSentimentRefresh(data)
    const nextExpiry = nextRefreshAt ? formatSentimentDate(nextRefreshAt.toISOString()) : null

    // No user-facing refresh here - comment sentiment only regenerates on its
    // refresh date, and a hard refresh is an admin-side action.
    return (
        <div>
            <div className="mb-3">
                <button onClick={onBack} className="ugc-btn-ghost">
                    <ArrowLeft size={15} /> Back to videos
                </button>
            </div>

            {/* Title */}
            <div className="d-flex align-items-start gap-3 mb-4">
                {app?.image_url ? (
                    <img src={app.image_url} alt={app.name} style={{ width: "48px", height: "48px", borderRadius: "12px", objectFit: "cover", flexShrink: 0 }} />
                ) : (
                    <div className="ugc-sent-cta-icon"><MessageSquareText size={20} color="#F6255A" /></div>
                )}
                <div style={{ minWidth: 0 }}>
                    <h3 className="text-white mb-1" style={{ fontWeight: 700, fontSize: "20px" }}>Audience Sentiment</h3>
                    <p style={{ color: "#9ca3af", fontSize: "13.5px", margin: 0, lineHeight: 1.5 }}>
                        What viewers say in the comments under {app?.name}'s top videos on YouTube, Instagram and TikTok.
                    </p>
                    {(lastGenerated || nextExpiry) && (
                        <div className="d-flex align-items-center gap-3 flex-wrap mt-2" style={{ fontSize: "12px", color: "#6b7280" }}>
                            {lastGenerated && <span className="d-inline-flex align-items-center gap-1"><Sparkles size={12} /> Generated {lastGenerated}</span>}
                            {nextExpiry && <span className="d-inline-flex align-items-center gap-1"><Clock size={12} /> Refreshes {nextExpiry}</span>}
                        </div>
                    )}
                </div>
            </div>

            {status === SENTIMENT_STATUS.LOADING && <LoadingState />}

            {status === SENTIMENT_STATUS.RUNNING && <RunningState appName={app?.name} data={data} />}

            {status === SENTIMENT_STATUS.LOCKED && <UpgradeNotice info={error || {}} />}

            {(status === SENTIMENT_STATUS.FAILED || status === SENTIMENT_STATUS.ERROR) && (
                <div className="text-center" style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: "16px", padding: "40px 20px" }}>
                    <AlertTriangle size={28} color="#ef4444" style={{ marginBottom: "12px" }} />
                    <h6 style={{ color: "#fff", fontWeight: 600, marginBottom: "8px" }}>Couldn't generate sentiment</h6>
                    <p style={{ color: "#9ca3af", fontSize: "13px", maxWidth: "440px", margin: "0 auto 16px" }}>
                        {typeof error === "string" ? error : "Something went wrong while analysing comments."}
                    </p>
                    <button onClick={onGenerate} className="ugc-btn-outline">Try Again</button>
                </div>
            )}

            {status === SENTIMENT_STATUS.IDLE && (
                <div className="text-center" style={{ background: "rgba(30,31,41,0.4)", borderRadius: "16px", border: "1px solid rgba(255,255,255,0.05)", padding: "48px 20px" }}>
                    <p style={{ color: "#9ca3af", fontSize: "13.5px", maxWidth: "440px", margin: "0 auto 16px" }}>
                        Sentiment hasn't been generated for {app?.name} yet.
                    </p>
                    <button onClick={onGenerate} className="ugc-btn-primary" style={{ margin: "0 auto", width: "auto" }}>
                        <Sparkles size={15} /> Generate Sentiment
                    </button>
                </div>
            )}

            {status === SENTIMENT_STATUS.READY && (
                <div className="d-flex flex-column gap-4">
                    {data?.refresh_blocked && (
                        <UpgradeNotice info={{ ...data, code: data.code || "refresh_requires_upgrade" }} variant="banner" />
                    )}

                    {isRunning && (
                        <div className="d-flex align-items-center gap-2" style={{ background: "rgba(246,37,90,0.06)", border: "1px solid rgba(246,37,90,0.2)", borderRadius: "12px", padding: "12px 16px", color: "#e8e8e8", fontSize: "13px" }}>
                            <Spinner size="sm" style={{ color: "#F6255A", flexShrink: 0 }} />
                            {data?.job_status === "refreshing"
                                ? "Updating with fresh comments - current results stay visible until the new run finishes."
                                : "Some platforms are still being analysed - they'll appear here as they finish."}
                        </div>
                    )}

                    {/* ── Overview ─────────────────────────────────── */}
                    {overall ? (
                        <div className="ugc-sent-overview-grid">
                            <Card style={{ padding: "22px" }}>
                                <div className="d-flex align-items-start justify-content-between gap-3 mb-3 flex-wrap">
                                    <NetScore score={overall.score} label="Overall net sentiment" />
                                    <SentimentChip sentiment={overall.overall_sentiment} />
                                </div>
                                <NetScoreTrack score={overall.score} />
                                <div className="d-flex gap-4 flex-wrap mt-4">
                                    <div>
                                        <div style={STAT_LABEL_STYLE}>Comments analysed</div>
                                        <div style={STAT_VALUE_STYLE}>{overall.comments_analysed ?? 0}</div>
                                    </div>
                                    <div>
                                        <div style={STAT_LABEL_STYLE}>Platforms</div>
                                        <div className="d-flex align-items-center gap-2" style={{ ...STAT_VALUE_STYLE, fontSize: "16px", minHeight: "26px" }}>
                                            {(overall.platforms || []).map(id => <PlatformBadge key={id} id={id} />)}
                                        </div>
                                    </div>
                                </div>
                            </Card>
                            <Card style={{ padding: "22px" }}>
                                <SectionTitle>Sentiment distribution</SectionTitle>
                                <SentimentBar percent={overall.percent} counts={overall.counts} height={14} />
                                <div className="mt-3">
                                    <SentimentLegend percent={overall.percent} counts={overall.counts} />
                                </div>
                                <p style={{ color: "#6b7280", fontSize: "12px", margin: "14px 0 0", lineHeight: 1.5 }}>
                                    Every comment is labelled individually - the split is counted from those labels, not estimated.
                                    Net sentiment is positive minus negative, as a share of labelled comments.
                                </p>
                            </Card>
                        </div>
                    ) : (
                        <div className="d-flex align-items-start gap-2" style={{ background: "rgba(30,31,41,0.4)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: "12px", padding: "14px 16px", color: "#9ca3af", fontSize: "13px", lineHeight: 1.5 }}>
                            <Hourglass size={15} style={{ flexShrink: 0, marginTop: "2px" }} />
                            No platform has enough comments for an overall score yet. Sentiment needs at least 20 usable comments on a platform.
                        </div>
                    )}

                    {/* ── By platform ──────────────────────────────── */}
                    {entries.length > 0 && (
                        <div>
                            <SectionTitle right={completed.length > 1 && <span style={{ fontSize: "12px", color: "#6b7280" }}>Select a platform for the deep dive</span>}>
                                By platform
                            </SectionTitle>
                            <div className="ugc-sent-platform-grid">
                                {entries.map(entry => (
                                    <PlatformSummaryCard
                                        key={entry.id}
                                        entry={entry}
                                        selected={entry.id === selectedPlatform}
                                        onSelect={setSelectedPlatform}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* ── Deep dive ────────────────────────────────── */}
                    {selectedEntry && (
                        <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: "24px" }}>
                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
                                <div className="d-flex align-items-center gap-2" style={{ fontSize: "17px" }}>
                                    <PlatformBadge id={selectedEntry.id} />
                                    <span style={{ color: "#6b7280", fontWeight: 500, fontSize: "15px" }}>deep dive</span>
                                </div>
                                {completed.length > 1 && (
                                    <div className="ugc-sent-toggle" role="tablist">
                                        {completed.map(e => (
                                            <button key={e.id} type="button" className={e.id === selectedPlatform ? "is-active" : ""} onClick={() => setSelectedPlatform(e.id)}>
                                                <PlatformBadge id={e.id} muted={e.id !== selectedPlatform} />
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <PlatformSentimentDetail
                                key={selectedEntry.id}
                                platformId={selectedEntry.id}
                                platformData={selectedEntry.data}
                                fullData={data}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default SentimentPanel
